/**
 * StreakRiskFallbackBadge (Plan 02-13, Task 2 / UI-SPEC §"<StreakRiskFallbackBadge>").
 *
 * Tablet idle dashboard fallback chip (D-08). When the 20:00 streak-risk
 * push fan-out is unable to deliver (zero live subscriptions OR
 * notifyMember threw — see Plan 02-06 streakRiskNudge), apps/api broadcasts
 * a `streak_risk_member` verb on the `chores` topic with the affected
 * memberId in the payload. The dashboard's WS handler (Plan 02-17) sets
 * `sessionStorage[streak-risk:<memberId>] = '1'` for that day; this badge
 * reads the flag at mount and renders "{{nick}} · streken i dag" if set.
 *
 * The flag is one-shot per Oslo date — the dashboard's WS handler is
 * responsible for clearing it on day rollover. This component is purely
 * a renderer; it does not subscribe to WS events itself (Plan 17 owns
 * the dashboard's WS layer).
 *
 * Plan 02-17 mounts on the leader strip near the affected member's tile.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/primitives/Icon';

interface StreakRiskFallbackBadgeProps {
  memberId: string;
  nick: string;
  className?: string;
}

function readFlag(memberId: string): boolean {
  try {
    return globalThis.sessionStorage?.getItem(`streak-risk:${memberId}`) === '1';
  } catch {
    return false;
  }
}

export function StreakRiskFallbackBadge({
  memberId,
  nick,
  className,
}: StreakRiskFallbackBadgeProps): ReactNode {
  const { t } = useTranslation();
  // Read once on mount. The Plan 02-17 dashboard re-mounts the badge
  // when its WS handler updates the flag, so a remount is the
  // re-render trigger.
  const [hasRisk, setHasRisk] = useState<boolean>(() => readFlag(memberId));

  useEffect(() => {
    setHasRisk(readFlag(memberId));
  }, [memberId]);

  if (!hasRisk) return null;
  // Stell_Kjokken (#160): kveldskortet i limegrønt med flamme. Skissens
  // undertekst («… holder 6 dager») trenger streak-lengden, som WS-rammen
  // ikke har, så den er utelatt. Limegrønt over kortflaten, ikke rett på
  // lerretet: ink på lime-bg i gløden holder ikke 4.5:1 i mørkt tema
  // (contrast.test.ts måler ink på lime-bg over kortet).
  return (
    <div className={['bg-card rounded-lg', className].filter(Boolean).join(' ')}>
      <div
        data-testid="streak-risk-fallback-badge"
        role="status"
        aria-live="polite"
        className="border-lime/50 bg-lime-bg flex items-center gap-3.5 rounded-lg border px-4.5 py-4"
      >
        <Icon.Flame size={30} className="text-accent-text shrink-0" />
        <span className="text-lg font-extrabold">{t('streaks.risk.fallbackChip', { nick })}</span>
      </div>
    </div>
  );
}
